import { DataHeader } from '@/components/dataHeader';
import DynamicHeightDiv from '@/components/DynamicHeightDiv';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { FilterBarWithActions } from '@/components/filters/filterBarWithActions';
import {
  MobileFiltersTrigger,
  useIsMobileFilterSheetActive,
} from '@/components/filters/mobileFilters';
import { Card } from '@/components/ui/smallCard';
import { Tabs } from '@/components/tabs';
import { useConfig } from '@/config/config';
import { FilterProvider } from '@/contexts/filter';
import { SearchContextProvider, useSearchContext } from '@/contexts/search';
import { useFilterParams } from '@/dataManagement/filterAdapter/useFilterParams';
import { useStringParam } from '@/hooks/useParam';
import { useUpdateViewParams } from '@/hooks/useUpdateViewParams';
import { cn } from '@/utils/shadcn';
import EntityDrawer from '@/routes/occurrence/search/views/browseList/ListBrowser';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFilters } from './filters';
import { AboutContent, ApiContent } from './helpTexts';
import { searchConfig } from './searchConfig';
import { Table } from './views/table';
import { SearchPageTree } from './views/tree';
import PageMetaData from '@/components/PageMetaData';
import { ChecklistKeyContext } from '../ChecklistKeyContext';

export function TaxonSearchPage(): React.ReactElement {
  const [filter, setFilter] = useFilterParams({
    filterConfig: searchConfig,
    paramsToRemove: ['offset', 'from'],
  });
  const config = useConfig();
  const intl = useIntl();

  return (
    <>
      <PageMetaData
        path="/taxon/search"
        title={intl.formatMessage({ id: 'speciesSearch.title' })}
        description={intl.formatMessage({ id: 'speciesSearch.description' })}
      />

      <SearchContextProvider searchContext={config.taxonSearch}>
        <FilterProvider filter={filter} onChange={setFilter}>
          <TaxonSearchPageInner
            datasetKey={config.taxonSearch?.checklistKey ?? config.defaultChecklistKey}
          />
        </FilterProvider>
      </SearchContextProvider>
    </>
  );
}

export function TaxonSearchPageInner({
  datasetKey = import.meta.env.PUBLIC_DEFAULT_CHECKLIST_KEY,
}: {
  datasetKey?: string;
}): React.ReactElement {
  const searchContext = useSearchContext();
  const { filters } = useFilters({ searchConfig, datasetKey });
  const defaultView = searchContext?.tabs?.[0] ?? 'table';
  const [view] = useStringParam({
    key: 'view',
    defaultValue: defaultView,
    hideDefault: true,
  });

  const visibleFilters = useMemo(() => {
    if (view === 'table') {
      return filters;
    } else if (view === 'tree') {
      return { taxonId: filters.taxonId };
    } else if (filters.q) {
      return { q: filters.q };
    } else {
      return {};
    }
  }, [filters, view]);

  const filterSheetActive = useIsMobileFilterSheetActive(visibleFilters);

  return (
    <ChecklistKeyContext.Provider value={{ datasetKey }}>
      <EntityDrawer />
      <DataHeader
        className="g-bg-white"
        title={<FormattedMessage id="catalogues.taxa" defaultMessage="Taxa" />}
        hasBorder
        aboutContent={<AboutContent />}
        apiContent={<ApiContent />}
        mobileFiltersTrigger={<MobileFiltersTrigger filters={visibleFilters} />}
      >
        {/* <TaxonViewTabs
          setView={setView}
          view={view}
          defaultView={defaultView}
          tabs={searchContext.tabs}
        /> */}
      </DataHeader>

      <section
        className={cn(
          'g-bg-white g-border-b g-border-slate-200',
          filterSheetActive && 'g-hidden sm:g-block'
        )}
      >
        <FilterBarWithActions filters={visibleFilters} className="g-px-4" hideMobileFilters />
      </section>

      <Views view={view} entityDrawerPrefix="t" className="g-py-2 g-px-4 g-bg-slate-100" />
    </ChecklistKeyContext.Provider>
  );
}

export function TaxonSearchInner({
  datasetKey = import.meta.env.PUBLIC_DEFAULT_CHECKLIST_KEY,
}: {
  datasetKey?: string;
}): React.ReactElement {
  const searchContext = useSearchContext();
  const { filters } = useFilters({ searchConfig, datasetKey });
  const defaultView = searchContext?.tabs?.[0] ?? 'table';
  const [view] = useStringParam({
    key: 'view',
    defaultValue: defaultView,
    hideDefault: true,
  });

  const visibleFilters = useMemo(() => {
    if (view === 'table') {
      return filters;
    } else if (view === 'tree') {
      return { taxonId: filters.taxonId };
    } else if (filters.q) {
      return { q: filters.q };
    } else {
      return {};
    }
  }, [filters, view]);

  return (
    <ChecklistKeyContext.Provider value={{ datasetKey }}>
      <ErrorBoundary showReportButton>
        <EntityDrawer />
        <Card>
          {/* <TaxonViewTabs
              setView={() => {}}
              view={view}
              defaultView={defaultView}
              tabs={searchContext.tabs}
            /> */}
          <div className="g-p2">
            <FilterBarWithActions filters={visibleFilters} />
          </div>
        </Card>

        <Views view={view} entityDrawerPrefix="t" className="g-py-2" />
      </ErrorBoundary>
    </ChecklistKeyContext.Provider>
  );
}

export function Views({
  view,
  className,
  entityDrawerPrefix,
}: {
  view?: string;
  className?: string;
  entityDrawerPrefix: string;
}) {
  const fixedHeight = ['table'].includes(view ?? '');
  return (
    <ErrorBoundary invalidateOn={view}>
      <div className={className}>
        {fixedHeight && (
          <DynamicHeightDiv minPxHeight={500}>
            {view === 'table' && <Table entityDrawerPrefix={entityDrawerPrefix} />}
          </DynamicHeightDiv>
        )}
        {!fixedHeight && (
          <DynamicHeightDiv
            minPxHeight={500}
            onlySetMinHeight
            className="g-bg-white g-flex-1 g-border g-border-solid g-basis-full g-h-1 g-flex g-flex-col g-overflow-auto g-p-4"
          >
            {view === 'tree' && <SearchPageTree />}
          </DynamicHeightDiv>
        )}
      </div>
    </ErrorBoundary>
  );
}

export function TaxonViewTabs({
  view,
  defaultView,
  tabs = ['table', 'tree'],
}: {
  setView: (view: string) => void;
  defaultView?: string;
  view?: string;
  tabs?: string[];
}) {
  const { getParams } = useUpdateViewParams(['from', 'sort', 'limit', 'offset']); // Removes 'from' and 'sort'

  return (
    <Tabs
      disableAutoDetectActive
      className="g-border-none"
      links={tabs.map((tab) => ({
        isActive: view === tab,
        to: { search: getParams(tab, defaultView).toString() },
        children: <FormattedMessage id={`search.tabs.${tab}`} defaultMessage={tab} />,
      }))}
    />
  );
}
